import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/authContext';
import jwt_decode from 'jwt-decode';
import { beginRequest, endRequest, markRevalidating } from '../utils/requestActivityStore';

// A password rotation can land mid-session, so any request may come back with
// PASSWORD_CHANGE_REQUIRED. Guarded against firing per in-flight request — a
// dashboard view easily has a dozen running at once and would otherwise stack a
// dozen identical toasts on the way out.
let passwordChangeRedirectInFlight = false;

const useHttp = (overrides = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const authCtx = useAuth();
  const addStoredRequest = authCtx?.addStoredRequest;
  const addIntermediateToken = authCtx?.addIntermediateToken;
  const logout = authCtx?.logout;
  const isLoggedIn = authCtx?.isLoggedIn;

  const handlePasswordChangeRequired = useCallback(() => {
    // Without a session this is the login or reset screen answering, where the
    // view shows the reason in place. Only an already-signed-in user needs to be
    // pulled out of the dashboard.
    if (!isLoggedIn || passwordChangeRedirectInFlight) return;
    passwordChangeRedirectInFlight = true;

    toast.error(
      'Aus Sicherheitsgründen musst du ein neues Passwort vergeben. Den Link dazu haben wir dir per E-Mail geschickt.',
      { autoClose: false }
    );
    logout?.();
    window.location.assign('/request-password-reset');
  }, [logout, isLoggedIn]);

  const normalizeRequiredRights = value => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean).map(v => String(v).trim()).filter(Boolean);
    if (typeof value === 'string') {
      const s = value.trim();
      return s ? [s] : [];
    }
    return [String(value).trim()].filter(Boolean);
  };

  // Determine which addIntermediateToken implementation to use
  const addIntermediateTokenFn = useMemo(
    () => overrides.addIntermediateToken || addIntermediateToken || (() => {}),
    [overrides.addIntermediateToken, addIntermediateToken]
  );

  const sendRequest = useCallback(
    async (
      requestConfig,
      toastMessage = null,
      onStored = null,
      displayInfo = null,
      onSuccess = null,
      successToastMessage = null
    ) => {
      setIsLoading(true);
      setError(null);

      const sanitizeQueryParams = params => {
        if (!params || typeof params !== 'object') return null;
        const out = {};
        Object.entries(params).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (typeof value === 'string' && value.trim() === '') return;
          if (Array.isArray(value)) {
            const cleaned = value.filter(v => v !== undefined && v !== null && String(v).trim() !== '');
            if (cleaned.length === 0) return;
            out[key] = cleaned.join(',');
            return;
          }
          if (typeof value === 'object') {
            // URLSearchParams would turn this into "[object Object]" which breaks many backends
            return;
          }
          out[key] = value;
        });
        return out;
      };

      // Build query params if present
      let url = import.meta.env.VITE_BE_URL + requestConfig.url;

      if (requestConfig.params) {
        const sanitized = sanitizeQueryParams(requestConfig.params);
        if (sanitized && Object.keys(sanitized).length > 0) {
          const queryParams = new URLSearchParams(sanitized).toString();
          url += '?' + queryParams;
        }
      }

      let body;
      if (requestConfig.body) {
        if (requestConfig.noJSON) body = requestConfig.body;
        else body = JSON.stringify(requestConfig.body);
      } else {
        body = null;
      }

      // Routine background traffic (counter badges, pollers) passes
      // `background: true` and stays out of the top request-activity bar - the
      // bar is reserved for work the user is actually waiting for.
      const background = requestConfig.background === true;

      if (!background) beginRequest();
      try {
        const response = await fetch(url, {
          method: requestConfig.method ? requestConfig.method : 'GET',
          headers: {
            // Lets the backend attribute API requests to this frontend for the
            // legacy vs. new management-frontend migration statistics.
            'X-Fz-Frontend': 'legacy',
            ...(requestConfig.headers ? requestConfig.headers : {}),
          },
          body: requestConfig.method !== 'GET' ? body : null, // Do not send body with GET requests
        });

        // Handle intermediate token from credentials auth
        const newIntermediateToken = response.headers.get('Intermediate-Token');
        if (newIntermediateToken) {
          try {
            const decodedToken = jwt_decode(newIntermediateToken);
            // The cardId in the token is already hashed by the server
            if (decodedToken.cardId) {
              addIntermediateTokenFn(decodedToken.cardId, newIntermediateToken);
            }
          } catch (error) {
            console.error('Error decoding intermediate token from header:', error);
          }
        }

        let data;
        if (requestConfig.returnBlob) {
          if (!response.ok) {
            const rawText = await response.text();
            let errorData = null;
            try {
              errorData = rawText ? JSON.parse(rawText) : null;
            } catch {
              errorData = rawText ? { message: rawText } : null;
            }

            setIsLoading(false);

            if (response.status === 403 && errorData?.code === 'PASSWORD_CHANGE_REQUIRED') {
              handlePasswordChangeRequired();
              const error = new Error('403: Password change required.');
              error.statusCode = 403;
              error.code = 'PASSWORD_CHANGE_REQUIRED';
              throw error;
            }

            if (
              response.status === 403 &&
              errorData?.code === 'INSUFFICIENT_RIGHTS' &&
              !requestConfig.noAutoStore
            ) {
              const requiredRights = normalizeRequiredRights(
                errorData?.requiredRights ??
                  errorData?.requiredRight ??
                  errorData?.missingRights ??
                  errorData?.missingRight
              );

              addStoredRequest?.(
                {
                  url: requestConfig.url,
                  method: requestConfig.method || 'GET',
                  headers: requestConfig.headers,
                  body: requestConfig.body,
                  params: requestConfig.params,
                  returnBlob: requestConfig.returnBlob,
                  noJSON: requestConfig.noJSON,
                  requiredRights,
                },
                toastMessage,
                onStored,
                displayInfo,
                onSuccess,
                successToastMessage
              );
              return null;
            }

            const errorMessage =
              errorData?.status === 'error' &&
              typeof errorData?.message === 'string' &&
              errorData.message.trim().length > 0
                ? errorData.message
                : response.statusText || 'Request failed.';
            const error = new Error(`${response.status}: ${errorMessage}`);
            error.statusCode = response.status;
            if (errorData?.code) error.code = errorData.code;
            if (errorData) error.data = errorData;
            throw error;
          }
          data = await response.blob();
        } else if (requestConfig.ignoreResponseBody && response.ok) {
          data = null;
        } else {
          const rawText = await response.text();
          try {
            data = rawText ? JSON.parse(rawText) : null;
          } catch (parseError) {
            if (response.ok) {
              throw parseError;
            }
            data = rawText ? { message: rawText } : null;
          }
          if (!response.ok) {
            setIsLoading(false);

            if (response.status === 403 && data?.code === 'PASSWORD_CHANGE_REQUIRED') {
              handlePasswordChangeRequired();
              const error = new Error('403: Password change required.');
              error.statusCode = 403;
              error.code = 'PASSWORD_CHANGE_REQUIRED';
              throw error;
            }

            if (
              response.status === 403 &&
              data?.code === 'INSUFFICIENT_RIGHTS' &&
              !requestConfig.noAutoStore
            ) {
              const requiredRights = normalizeRequiredRights(
                data?.requiredRights ?? data?.requiredRight ?? data?.missingRights ?? data?.missingRight
              );

              // Store the original request
              const requestToStore = {
                url: requestConfig.url,
                method: requestConfig.method || 'GET',
                headers: requestConfig.headers,
                body: requestConfig.body,
                params: requestConfig.params,
                returnBlob: requestConfig.returnBlob,
                noJSON: requestConfig.noJSON,
                requiredRights,
              };

              // Add to stored requests array with toast message, callback and display info
              addStoredRequest?.(
                requestToStore,
                toastMessage,
                onStored,
                displayInfo,
                onSuccess,
                successToastMessage
              );

              // Don't throw error for INSUFFICIENT_RIGHTS as the request is stored
              // console.log('Zusätzliche Authentifizierung für Anfrage ', requestConfig.url, ' erforderlich: ', data.requiredRights);
              return null; // Return null to indicate the request was stored
            }

            const errorMessage =
              data?.status === 'error' && typeof data?.message === 'string' && data.message.trim().length > 0
                ? data.message
                : response.statusText || 'Request failed.';
            const error = new Error(`${response.status}: ${errorMessage}`);
            error.statusCode = response.status;
            if (data?.code) error.code = data.code;
            if (data) error.data = data;
            throw error;
          }
        }

        // The backend served cached data and is reconciling it with the database in
        // the background -> show the reconcile state on the top activity bar.
        if (!background && response.headers.get('X-FZ-Cache') === 'stale') {
          markRevalidating();
        }

        setIsLoading(false);
        return data;
      } catch (err) {
        setIsLoading(false);
        setError(err.message || 'Etwas ist schiefgelaufen!');
        console.error('error in useHttp', err);
        throw err; // Re-throw to allow calling code to handle the error
      } finally {
        if (!background) endRequest();
      }
    },
    [addIntermediateTokenFn, addStoredRequest, handlePasswordChangeRequired]
  );

  return {
    isLoading,
    error,
    setError,
    sendRequest,
  };
};

export const useHTTP = useHttp;
export default useHttp;
